/* */
/* * LocationMap: div with map that can show geojson and/or a pin */
/* * MapAndLocationName: adds name, coordinates, and buttons under LocationMap */
/* * LocationMapCard: puts MapAndLocationName on a card */
/* */
import { Viewport, viewport } from '@placemarkio/geo-viewport';
import MultilingualText from 'components/Multilingual/MultilingualText';
import IntlMessages from 'helpers/IntlMessages';
import { parseNominatimLocationName } from 'helpers/utils-typescript';
import MapMarkerIcon from 'mdi-react/MapMarkerIcon';
import { GeoJson, Map, Marker, Point } from 'pigeon-maps';
import { maptiler } from 'pigeon-maps/providers';
import React, { Fragment, ReactElement, useState } from 'react';
import { Card } from 'react-bootstrap';
import { Scalars } from 'services/graphqltypes';
import MapAttribution from './MapAttribution';

const maptilerProvider = maptiler(import.meta.env.VITE_MAPTILER_API_KEY, 'topo');

interface LocationMapProps {
  coordinates?: Point,
  geoJson?: Scalars['JSON'],
  boundingBox?: Scalars['JSON'],
  /** set static height of the map */
  mapHeight: any,
  showMarker?: boolean,
  markerIcon?: ReactElement, // use map default Marker if markerIcon is not specified
}

/** A div with pigeon map that can show a geojson polygon and/or a pin on provided coordinates
 * IMPORTANT: Make sure the div is visible on first render (if you use tabs, then use mountOnEnter),
 * otherwise initial zoom can not be computed.
 */
export const LocationMap = ({
  coordinates,
  geoJson,
  boundingBox,
  mapHeight,
  showMarker = true,
  markerIcon,
}: LocationMapProps) => {
  const [mapInitialCenterZoom, setMapInitialCenterZoom] = useState<Viewport>({ center: [null, null], zoom: null });

  /* */
  /* Map center and zoom based on the provided bounding box */
  /* */
  const setWidthHeightZoom = React.useCallback(node => {
    if (node !== null && boundingBox) {
      const mapWidth = node.clientWidth as number;
      const mapHeight = node.clientHeight as number;
      setMapInitialCenterZoom(viewport(boundingBox as any, [mapWidth, mapHeight]));
    } else if (!boundingBox) { // if there is no geojson/bounding box, we set static zoom
      setMapInitialCenterZoom({ center: [coordinates[1], coordinates[0]], zoom: 16 });
    }
  }, [boundingBox, coordinates]);

  return (
    <div ref={setWidthHeightZoom} className='card-img' style={{ height: mapHeight, overflow: 'hidden' }} >
      {mapInitialCenterZoom.zoom > 0 &&
        <Map
          boxClassname='flex-fill'
          defaultCenter={[mapInitialCenterZoom.center[1], mapInitialCenterZoom.center[0]]}
          defaultZoom={mapInitialCenterZoom.zoom}
          provider={maptilerProvider}
          dprs={[1, 2]} // this provider supports HiDPI tiles
          attribution={<MapAttribution />}
        >

          {geoJson &&
            <GeoJson
              data={{
                type: 'FeatureCollection',
                features: [{
                  type: 'Feature',
                  geometry: geoJson
                }]
              }}
            />
          }

          {showMarker &&
            <Marker
              anchor={coordinates}
              width={40}
              color={'#60b6aa'}
              hover={false} // remove default hover effect, and make marker not clickable
              style={{ cursor: 'default' }}
            >
              {markerIcon}
            </Marker>
          }
        </Map>
      }
    </div >
  )
}

type MapAndLocationNameProps = {
  district?: Scalars['JSON'], // for specimen
  coordinates?: Point,
  geoJson?: Scalars['JSON'],
  boundingBox?: Scalars['JSON'],
  /** set static height of the map */
  mapHeight?: any,
  /** set containerHeight if want to have map with dynamic height expand to fill the container */
  containerHeight?: string,
  address?: Scalars['JSON'], // for event
  locationName?: string, // for event
  showMarker?: boolean,
  markerIcon?: ReactElement, // use map default Marker if markerIcon is not specified
  showMapOnly?: boolean, // only show map, don't show address, coordinates, ...
};

/** A div with borderless map, custom-defined (or default) pin on provided coordinates, and address or location name underneath. View in map button at the bottom */
export const MapAndLocationName = ({
  district,
  coordinates,
  geoJson,
  boundingBox,
  mapHeight,
  containerHeight,
  address,
  showMarker = true,
  markerIcon,
  locationName,
  showMapOnly = false,
}: MapAndLocationNameProps) => {

  return (
    <div className='d-flex flex-column' style={{ height: containerHeight }}>

      <LocationMap
        boundingBox={boundingBox}
        coordinates={coordinates}
        geoJson={geoJson}
        mapHeight={mapHeight}
        markerIcon={markerIcon}
        showMarker={showMarker}
      />

      {/* Location Info */}
      {
        !showMapOnly &&
        <Fragment>
          <div className="px-3 d-flex flex-row align-items-center my-4">
            <div className='ml-auto'>
              <MapMarkerIcon size={'45'} className="color-theme-1" />
            </div>
            <div className='d-flex flex-column mr-auto ml-2'>
              {/* for specimen, show district */}
              {district &&
                <div className='mr-auto h4'>
                  <MultilingualText value={district} />
                </div>
              }
              {/* for event location, show address if there is any, if not, show locationName (banner will show location name) */}
              {address ?
                <div className='mr-auto'>
                  <div className='h4'>
                    {parseNominatimLocationName(
                      locationName,
                      address,
                      { x: coordinates[0], y: coordinates[1] },
                      false, false
                    )} <br />
                  </div>

                  <div className='h5'>
                    {address?.road && `${address?.road} `}
                    {address?.house_number && `${address?.house_number}, `}

                    {address?.suburb && `${address.suburb}, `}
                    {address?.town && `${address.town}, `}

                    {(address?.state || address.country) && <br />}
                    {address?.state && `${address.state}, `}
                    {address?.country && `${address.country}`}
                  </div>

                </div>
                : <div>
                  {locationName}
                </div>
              }

              {/* coordinates if available */}
              {coordinates[0] && coordinates[1] &&
                <div className='mr-auto' style={{ overflowWrap: 'anywhere' }}>
                  {`${coordinates[0].toFixed(5) || '?'}, ${coordinates[1].toFixed(5) || '?'}`}
                </div>
              }
            </div>
          </div>

          {/* Open in map button */}
          {coordinates[0] && coordinates[1] &&
            <div className='pb-4 px-4'>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${coordinates[0]}%2C${coordinates[1]}`}
                className='btn btn-outline-primary btn-block'
                target="_blank"  // open url in a new tab
                rel="noreferrer" // recommenced by lint for security reason
              >
                <IntlMessages id='location.open-in-map' />
              </a>
            </div>
          }
        </Fragment>
      }
    </div>
  );
};

/** Card with map, location name, "Open in map" button */
const LocationMapCard = ({ district, coordinates, mapHeight, containerHeight, address,
  markerIcon, locationName }: MapAndLocationNameProps) => {
  return (
    <Card className='mb-3'>
      <MapAndLocationName
        coordinates={coordinates}
        address={address}
        district={district}
        locationName={locationName}
        mapHeight={mapHeight}
        containerHeight={containerHeight}
        markerIcon={markerIcon}
      />
    </Card>
  );
};

export default LocationMapCard;
